import { atom, selector, selectorFamily } from 'recoil';
import { getConfig } from '@/config/config';
import { MAX_INACTIVITY_TO_RECONNECT_MS, MINIMUM_MESSAGES_TO_SHOW_SURVEY, } from '@/config/constants';
import { showSurveySelector, integrationIdSelector, isStickySessionSelector, userConfigState, visitorInformationSelector, welcomeScreenSelector, rateResponsesSelector, avatarSelector, } from '@/state/config';
import { AutopilotResponseCode, } from '@/ts/message';
import { getTimestamp, isCustomSurveyEvent } from '@/util/utils';
import { socketIdState, webChatStateState, connectionState } from './socket';
export const sessionState = atom({
    key: 'sessionState',
    default: {},
});
/**
 * Session id from localStorage which is synchronized by the `useSession` hook
 */
export const sessionIdFromStorageState = atom({
    key: 'sessionIdFromStorageState',
    default: null,
});
export const sessionIdSelector = selector({
    key: 'sessionIdSelector',
    get: ({ get }) => {
        const config = get(sessionState);
        return config === null || config === void 0 ? void 0 : config.session_id;
    },
});
export const isSessionExpiredSelector = selector({
    key: 'isSessionExpiredSelector',
    get: ({ get }) => {
        const state = get(sessionState);
        return (state === null || state === void 0 ? void 0 : state.status) === "expired" /* SessionStatus.Expired */;
    },
});
export const isHumanChatSelector = selector({
    key: 'isHumanChatSelector',
    get: ({ get }) => {
        const state = get(sessionState);
        return state === null || state === void 0 ? void 0 : state.is_service_desk;
    },
});
export const errorSessionSelector = selector({
    key: 'errorSessionSelector',
    get: ({ get }) => {
        const state = get(sessionState);
        return state === null || state === void 0 ? void 0 : state.error;
    },
});
export const contextSelector = selector({
    key: 'contextSelector',
    get: ({ get }) => {
        const state = get(sessionState);
        return state === null || state === void 0 ? void 0 : state.context;
    },
});
export const userSelector = selector({
    key: 'userSelector',
    get: ({ get }) => {
        const context = get(contextSelector);
        return context === null || context === void 0 ? void 0 : context.user;
    },
});
// Compose state and selector
export const composeState = atom({
    key: 'composeState',
    default: null,
});
export const leadersState = atom({
    key: 'leadersState',
    default: {},
});
export const isLeaderSelector = selector({
    key: 'isLeaderSelector',
    get: ({ get }) => {
        const isStickySession = get(isStickySessionSelector);
        const leaders = get(leadersState);
        const socketId = get(socketIdState);
        const sessionId = get(sessionIdSelector);
        // If sticky sessions is disabled, isLeader is always true
        // Otherwise check that the session id exists in the leaders list
        return (!isStickySession ||
            (sessionId && socketId && leaders[sessionId] === socketId));
    },
});
/**
 * Returns true if the welcome form needs to be displayed. This could be true
 * if there are required fields or a disclaimer and the user hasn't updated it yet
 */
export const showWelcomeFormSelector = selector({
    key: 'showWelcomeFormSelector',
    get: ({ get }) => {
        var _a;
        const { disclaimer, fields } = get(visitorInformationSelector);
        const context = get(contextSelector);
        // Hide the form if it was already completed
        if ((_a = context === null || context === void 0 ? void 0 : context.global) === null || _a === void 0 ? void 0 : _a.visitor_form_completed) {
            return false;
        }
        // Hide form if there are no visitor fields and disclaimer
        if (!fields && !(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.enabled)) {
            return false;
        }
        return true;
    },
});
/**
 * Returns the automatic message that matches from the page_triggers and
 * `welcome_trigger_message`.
 */
export const triggerMessageSelector = selector({
    key: 'triggerMessageSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        // Check if there is a page trigger with a welcome trigger message
        const pageTriggers = config.page_triggers;
        if (pageTriggers) {
            for (const pageTrigger in pageTriggers) {
                const triggerRegExp = new RegExp(pageTrigger);
                const message = pageTriggers[pageTrigger].trigger_message;
                if (triggerRegExp.test(config.clientUrl) && message) {
                    return { text: message };
                }
            }
        }
        // Check the default welcome trigger message
        if (config.welcome_trigger_message) {
            return { text: config.welcome_trigger_message };
        }
        return null;
    },
});
/**
 * Returns the automatic message to send only if it's the right time to send it.
 * It will return null if the widget is closed, there are chat events, there is
 * a welcome form or a welcome screen.
 */
export const automaticMessageSelector = selector({
    key: 'automaticMessageSelector',
    get: ({ get }) => {
        var _a;
        const triggerMessage = get(triggerMessageSelector);
        const sessionId = get(sessionIdSelector);
        const showWelcomeForm = get(showWelcomeFormSelector);
        const webChatState = get(webChatStateState);
        const isWelcomeScreenEnabled = (_a = get(welcomeScreenSelector)) === null || _a === void 0 ? void 0 : _a.enabled;
        const hasChatEvents = get(chatEventCountSelector) > 0;
        // Do not send the automatic message if there isn't one to send,
        // there are events, the widget is close, there is a welcome form or a welcome screen
        if (!triggerMessage ||
            hasChatEvents ||
            showWelcomeForm ||
            !sessionId ||
            webChatState !== "synchronized" /* WebChatState.Synchronized */ ||
            isWelcomeScreenEnabled) {
            return null;
        }
        return triggerMessage;
    },
});
/**
 * Returns the key to use when saving the automatic message
 */
export const automaticMessageStorageKeySelector = selector({
    key: 'automaticMessageStorageKeySelector',
    get: ({ get }) => {
        const integrationId = get(integrationIdSelector);
        const sessionId = get(sessionIdSelector);
        return `moveo_${integrationId}_${sessionId}_message`;
    },
});
export const composeSelector = selector({
    key: 'composeSelector',
    get: ({ get }) => {
        const compose = get(composeState);
        if ((compose === null || compose === void 0 ? void 0 : compose.action) !== 'start') {
            return null;
        }
        // FIXME: Remove this once orchestrator sends a default avatar
        if (!compose.author_avatar) {
            compose.author_avatar = getConfig().BRAIN_AVATAR;
        }
        return compose;
    },
});
/**
 * This holds all the messages and activies in a chat window
 * It will include, user messages, agent or brain responses,
 * Member join/member left, handover, etc...
 */
export const chatEventsState = atom({
    key: 'chatEventsState',
    default: [],
});
export const lastBrainResponsesState = atom({
    key: 'lastBrainResponsesState',
    default: [],
});
export const lastBrainResponseResolveSelector = selector({
    key: 'lastBrainResponseResolveSelector',
    get: ({ get }) => {
        const events = get(lastBrainResponsesState);
        return events.some((e) => e.response.type === 'close');
    },
});
export const lastBrainResponseDefaultSurveySelector = selector({
    key: 'lastBrainResponseDefaultSurveySelector',
    get: ({ get }) => {
        const events = get(lastBrainResponsesState);
        return events.some((e) => e.response.type === 'survey' && !isCustomSurveyEvent(e.response));
    },
});
export const showCompactResolved = selector({
    key: 'showCompactResolved',
    get: ({ get }) => {
        const events = get(lastBrainResponsesState);
        return events.some((e) => e.event_type === 'brain_message' &&
            e.response.type === 'survey' &&
            isCustomSurveyEvent(e.response));
    },
});
export const afterSurveyAction = atom({
    key: 'afterSurveyAction',
    default: null,
});
/**
 * Returns all the chat events but the first in case it's an automatic message
 */
export const chatEventsSelector = selector({
    key: 'chatEventsSelector',
    get: ({ get }) => {
        var _a;
        const events = get(chatEventsState);
        const triggerMessage = (_a = get(triggerMessageSelector)) === null || _a === void 0 ? void 0 : _a.text;
        return events.filter((event, i) => {
            if (!triggerMessage || i > 0 || event.event_type !== 'user_message') {
                return true;
            }
            const userMessage = event;
            if (triggerMessage && triggerMessage !== userMessage.text) {
                return true;
            }
            // If the event is an automatic message, remove it from the list
            return false;
        });
    },
});
export const brainEventsQueue = atom({
    key: 'brainEventsQueue',
    default: [],
});
export const nextQueuedEventSelector = selector({
    key: 'firstQueuedMessageSelector',
    get: ({ get }) => {
        const messagesQueue = get(brainEventsQueue);
        return messagesQueue[0];
    },
});
export const chatEventCountSelector = selector({
    key: 'chatEventCountSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        return events.length;
    },
});
export const hasEventsSelector = selector({
    key: 'hasEventsSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        return events.length > 0;
    },
});
/**
 * Return true if the last message sent by an agent or brain from the chat Events
 * was read.
 * This will be used to know if we need to mark messages as read.
 */
export const isLastMessageReadSelector = selector({
    key: 'isLastMessageReadSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        const messages = events.filter((e) => e.event_type === 'agent_message' || e.event_type === 'brain_message');
        const lastMessage = messages.pop();
        if (!lastMessage || 'read_at' in lastMessage) {
            return true;
        }
        return false;
    },
});
/**
 * Returns true if the last message from the brain or agent is not read yet.
 */
export const hasUnreadMessagesSelector = selector({
    key: 'hasUnreadMessagesSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        const messages = events.filter((e) => e.event_type === 'agent_message' || e.event_type === 'brain_message');
        const lastMessage = messages.pop();
        return lastMessage && !('read_at' in lastMessage);
    },
});
/**
 * Will show the survey only if user has sent at least a minimum number of messages
 */
export const shouldDisplaySurveySelector = selector({
    key: 'shouldDisplaySurveySelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        const skipSurvey = get(skipSurveyState) || !get(showSurveySelector);
        const lastBrainResponseSurvey = get(lastBrainResponseDefaultSurveySelector);
        const showIntegrationSurvey = events.filter((e) => e.event_type === 'user_message').length >=
            MINIMUM_MESSAGES_TO_SHOW_SURVEY && !skipSurvey;
        return showIntegrationSurvey || lastBrainResponseSurvey;
    },
});
// Check if the last message from the brain is the one from the request_id
export const isLastMessageFromBrainSelector = selectorFamily({
    key: 'isLastMessageFromBrainSelector',
    get: (requestId) => ({ get }) => {
        var _a;
        const events = get(chatEventsState);
        const brainMessages = events.filter((e) => e.event_type === 'brain_message');
        return ((_a = brainMessages[brainMessages.length - 1]) === null || _a === void 0 ? void 0 : _a.request_id) === requestId;
    },
});
/**
 * Return true if the brain response from `eventId` comes before
 * the last user message.
 */
export const isResponseDisabledSelector = selectorFamily({
    key: 'lastUserMessageSelector',
    get: (eventId) => ({ get }) => {
        const events = get(chatEventsState);
        let found = false;
        for (const event of events) {
            if (found && event.event_type === 'user_message') {
                return true;
            }
            if (event.event_id === eventId) {
                found = true;
            }
        }
        return false;
    },
});
/**
 * Return true if the session includes a brain
 */
export const isTalkingToBrainSelector = selector({
    key: 'isTalkingToBrainSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        let response = 0;
        events.forEach((e) => {
            if (e.event_type === 'member_join' && e.author_type === 'brain') {
                response += 1;
            }
            else if (e.event_type === 'member_leave' && e.author_type === 'brain') {
                response -= 1;
            }
        });
        return response > 0 || events.length === 0;
    },
});
/**
 * Return true if the session includes an agent
 */
export const assignedAgentIdSelector = selector({
    key: 'assignedAgentIdSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        let response = null;
        events.forEach((e) => {
            if (e.event_type === 'member_join' && e.author_type === 'agent') {
                response = e.author_id;
            }
            else if (e.event_type === 'member_leave' && e.author_type === 'agent') {
                response = null;
            }
        });
        return response;
    },
});
export const isUnassignedSelector = selector({
    key: 'isUnassignedSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        const isTalkingToAgent = !!get(assignedAgentIdSelector);
        const isTalkingToBrain = get(isTalkingToBrainSelector);
        return !isTalkingToAgent && !isTalkingToBrain && events.length > 0;
    },
});
export const overviewSelector = selector({
    key: 'overviewSelector',
    get: ({ get }) => {
        var _a, _b, _c, _d;
        const overview = get(sessionOverviewState);
        const isUnassigned = get(isUnassignedSelector);
        const assignedAgentId = get(assignedAgentIdSelector);
        const sortedAgents = (_c = (_b = [...((_a = overview === null || overview === void 0 ? void 0 : overview.agents) !== null && _a !== void 0 ? _a : [])]) === null || _b === void 0 ? void 0 : _b.sort((a, b) => {
            if (a.status === 'online' && b.status !== 'online') {
                return -1;
            }
            else if (a.status !== 'online' && b.status === 'online') {
                return 1;
            }
            else {
                return 0;
            }
        })) !== null && _c !== void 0 ? _c : [];
        if (isUnassigned && overview) {
            return {
                agents: sortedAgents,
                queuePosition: (_d = overview.queue_position) !== null && _d !== void 0 ? _d : 0,
                waitingTime: overview.wait_time_avg
                    ? overview.queue_position * overview.wait_time_avg
                    : 0,
            };
        }
        if (assignedAgentId) {
            return {
                agents: sortedAgents.filter((a) => a.agent_id === assignedAgentId),
                queuePosition: 0,
                waitingTime: null,
            };
        }
        return {
            agents: [],
            queuePosition: 0,
            waitingTime: null,
        };
    },
});
export const isWidgetOpenState = atom({
    key: 'isWidgetOpenState',
    default: false,
});
export const isWidgetExpandedState = atom({
    key: 'isWidgetExpandedState',
    default: false,
});
export const isMobileState = atom({
    key: 'isMobileState',
    default: false,
});
export const openingMessageState = atom({
    key: 'openingMessageState',
    default: false,
});
export const lastActivityInPageState = atom({
    key: 'lastActivityInPageState',
    default: 0,
});
export const lastActivityInWidgetState = atom({
    key: 'lastActivityInWidgetState',
    default: 0,
});
export const hideWebchatState = atom({
    key: 'hideWebchatState',
    default: false,
});
/**
 * Selector to determine if a visitor is considered present based on their activity.
 *
 * This selector calculates the time elapsed since the last visitor activity
 * and compares it to a predefined threshold (`MAX_INACTIVITY_TO_RECONNECT_MS`).
 * If the time elapsed is within the threshold, the visitor is considered present.
 *
 */
export const isVisitorPresentSelector = selector({
    key: 'isVisitorPresent',
    get: ({ get }) => {
        const lastActivity = get(lastActivityInWidgetState);
        // If there is no last activity, the user is considered present
        if (!lastActivity) {
            return true;
        }
        const currentTime = getTimestamp();
        const timeSinceLastActivity = currentTime - lastActivity;
        // The user is considerate present if he does something in the last 45 minutes
        return timeSinceLastActivity < MAX_INACTIVITY_TO_RECONNECT_MS;
    },
});
export const chatModalState = atom({
    key: 'chatModalState',
    default: null,
});
export const webviewModalState = atom({
    key: 'webviewModalState',
    default: null,
});
export const sessionOverviewState = atom({
    key: 'sessionOverviewState',
    default: null,
});
export const availableAgentsSelector = selector({
    key: 'availableAgentsSelector',
    get: ({ get }) => {
        var _a;
        const overview = get(sessionOverviewState);
        return (_a = overview === null || overview === void 0 ? void 0 : overview.agents) !== null && _a !== void 0 ? _a : [];
    },
});
export const isMobileSelector = selector({
    key: 'isMobileSelector',
    get: ({ get }) => {
        const state = get(isMobileState);
        return state;
    },
});
export const imageLoadedState = atom({
    key: 'imageLoaded',
    default: false,
});
export const skipSurveyState = atom({
    key: 'skipSurvey',
    default: false,
});
export const filesToUploadState = atom({
    key: 'filesToUploadState',
    default: [],
});
export const filesToUploadLengthSelector = selector({
    key: 'filesToUploadLengthSelector',
    get: ({ get }) => {
        const files = get(filesToUploadState);
        return files.length;
    },
});
// check if a file contains an upload_url
export const fileContainsUploadUrlSelector = selectorFamily({
    key: 'fileContainsUploadUrlSelector',
    get: (fileId) => ({ get }) => {
        const files = get(filesToUploadState);
        const file = files.find((f) => f.file_id === fileId);
        return !!(file === null || file === void 0 ? void 0 : file.upload_url);
    },
});
// check if a file has upload_error
export const fileHasUploadErrorSelector = selectorFamily({
    key: 'fileHasUploadErrorSelector',
    get: (fileId) => ({ get }) => {
        const files = get(filesToUploadState);
        const file = files.find((f) => f.file_id === fileId);
        return !!(file === null || file === void 0 ? void 0 : file.upload_error);
    },
});
export const isUploadingFileState = atom({
    key: 'isUploadingFileState',
    default: false,
});
export const disableInputSelector = selector({
    key: 'disableInputSelector',
    get: ({ get }) => {
        const isUploading = get(isUploadingFileState);
        const isTalkingToBrain = get(isTalkingToBrainSelector);
        return isUploading || isTalkingToBrain;
    },
});
/**
 * Selector to determine if we should show a vote bubble.
 *
 * This selector calculates if the selected message belong to a brain,
 *  was answered by a collection, is last in the list and no other messages are in queue
 */
export const showMessageFeedbackSelector = selectorFamily({
    key: 'showMessageFeedbackSelectorSelector',
    get: (eventId) => ({ get }) => {
        var _a, _b;
        const brainEventsinQueue = get(brainEventsQueue);
        const rateResponses = get(rateResponsesSelector);
        if (!rateResponses) {
            return false;
        }
        const events = get(chatEventsState);
        const eventIndex = events.findIndex((e) => e.event_id === eventId);
        const event = events[eventIndex];
        const isResponseFromCollection = ((_a = event === null || event === void 0 ? void 0 : event.collection) === null || _a === void 0 ? void 0 : _a.response_code) === AutopilotResponseCode.OK ||
            ((_b = event === null || event === void 0 ? void 0 : event.collection) === null || _b === void 0 ? void 0 : _b.response_code) === AutopilotResponseCode.OK_FALLBACK;
        if (brainEventsinQueue.length > 0) {
            return false;
        }
        if ((event === null || event === void 0 ? void 0 : event.event_type) === 'brain_message') {
            if (event.response.type === 'text' &&
                eventIndex === events.length - 1 &&
                isResponseFromCollection &&
                event.source === 'live') {
                return true;
            }
        }
        return false;
    },
});
/**
 * Selector to determine if we should show external urls.
 *
 * This selector calculates if the selected message has external urls,
 * is last in the list and no other messages are in queue
 */
export const showExternalUrlsSelector = selectorFamily({
    key: 'showExternalUrlsSelectorSelector',
    get: (eventId) => ({ get }) => {
        const events = get(chatEventsState);
        const eventIndex = events.findIndex((e) => e.event_id === eventId);
        const brainEventsinQueue = get(brainEventsQueue);
        const event = events[eventIndex];
        const brainMessageEvents = events.filter((e) => e.event_type === 'brain_message' && e.request_id === (event === null || event === void 0 ? void 0 : event.request_id));
        if (brainEventsinQueue.length > 0) {
            return false;
        }
        if (brainMessageEvents.length > 1) {
            return (brainMessageEvents[brainMessageEvents.length - 1].event_id === eventId);
        }
        return true;
    },
});
export const lastEventSelector = selector({
    key: 'lastEventSelector',
    get: ({ get }) => {
        const events = get(chatEventsState);
        return events[events.length - 1];
    },
});
export const isPreviousMessageFromSameAuthorSelector = selectorFamily({
    key: 'isPreviousMessageFromSameAuthorSelector',
    get: (eventId) => ({ get }) => {
        var _a;
        const events = get(chatEventsState);
        const eventIndex = events.findIndex((e) => e.event_id === eventId);
        const previousEvent = events[eventIndex - 1];
        return ((previousEvent === null || previousEvent === void 0 ? void 0 : previousEvent.author_id) === ((_a = events[eventIndex]) === null || _a === void 0 ? void 0 : _a.author_id) &&
            previousEvent.event_type === 'agent_message');
    },
});
export const isLastMemberLeftEvent = selectorFamily({
    key: 'isLastMemberLeftEvent',
    get: (eventId) => ({ get }) => {
        var _a;
        const events = get(chatEventsState);
        const memberLeftEvents = events.filter((e) => e.event_type === 'member_leave');
        return (((_a = memberLeftEvents[memberLeftEvents.length - 1]) === null || _a === void 0 ? void 0 : _a.event_id) === eventId);
    },
});
export const isEmailFormSubmittedState = atom({
    key: 'isEmailFormSubmittedState',
    default: false,
});
export const isEmailFormSubmittedSelector = selector({
    key: 'isEmailFormSubmittedSelector',
    get: ({ get }) => {
        return get(isEmailFormSubmittedState);
    },
});
export const emailState = atom({
    key: 'emailState',
    default: '',
});
export const headerSelector = selector({
    key: 'headerSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        const status = get(connectionState);
        const events = get(chatEventsState);
        const isTalkingToBrain = get(isTalkingToBrainSelector);
        const avatar = get(avatarSelector);
        if (isTalkingToBrain || events.length === 0) {
            return {
                show: true,
                status,
                title: config.header_title,
                avatar,
            };
        }
        const assignedAgentId = get(assignedAgentIdSelector);
        const agents = get(availableAgentsSelector);
        const assignedAgent = agents.find((a) => a.agent_id === assignedAgentId);
        if (assignedAgent) {
            return {
                show: true,
                status: assignedAgent.status,
                title: assignedAgent.name,
                avatar: assignedAgent.avatar,
            };
        }
        return {
            show: true,
            status,
            title: config.header_title,
            avatar,
        };
    },
});
